/* eslint-disable react/jsx-props-no-spreading */
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import {
  ChangeEvent,
  ComponentProps,
  ElementRef,
  FormEvent,
  forwardRef,
  ReactNode,
  useRef,
  useState,
  type JSX,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from '~/components/Icon';
import { useUniqueId } from '~/utils/compute';
import Field from './Field';

const Trigger = forwardRef<
  ElementRef<typeof PopoverTrigger>,
  ComponentProps<typeof PopoverTrigger> & { label: string }
>(({ className, children, id, label, ...props }, ref) => (
  <PopoverTrigger
    ref={ref}
    className={twMerge(
      'group',
      // TODO: figure out how to have the trigger not collapse when there is no value
      // this is a hack to prevent the trigger from collapsing when there is no value
      'after:invisible after:content-["."]',
      'relative flex w-full items-center justify-between rounded-md border border-outline px-4 pt-5 pb-1 text-left [&>span]:line-clamp-1',
      'focus:border-primary focus:ring-1 focus:ring-primary focus:outline-hidden',
      'data-[state="open"]:border-primary data-[state="open"]:ring-1 data-[state="open"]:ring-primary data-[state="open"]:outline-hidden',
      'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200',
      'data-[valid=false]:border-error data-[valid=false]:focus:ring-error data-[valid=false]:data-[state="open"]:border-error data-[valid=false]:data-[state="open"]:ring-error',

      className,
    )}
    id={id}
    {...props}
  >
    <Field.Label
      htmlFor={id}
      className={twMerge(
        'pointer-events-none absolute left-0 ml-4 px-0 pt-0 pb-1 duration-100 ease-linear',
        // field has value
        '-translate-y-4 text-xs',
        // field is empty and opened
        'group-data-[state="open"]:group-data-[empty=true]:-translate-y-4 group-data-[state="open"]:group-data-[empty=true]:text-xs',
        // field is empty
        'group-data-[empty=true]:-translate-y-2 group-data-[empty=true]:pb-0 group-data-[empty=true]:text-sm group-data-[empty=true]:text-gray-500',
      )}
    >
      {label}
    </Field.Label>
    {children}
    <Icon
      icon="ChevronDown"
      className="absolute top-4 right-4 size-4 text-gray-500 transition-transform group-data-[state=open]:-rotate-180"
    />
  </PopoverTrigger>
));
Trigger.displayName = 'Combobox.Trigger';

const Search = forwardRef<
  React.ElementRef<typeof Command.Input>,
  React.ComponentPropsWithoutRef<typeof Command.Input>
>(({ className, ...props }, ref) => (
  <div className="flex items-center gap-2 border-b px-3">
    <Icon icon="MagnifyingGlass" className="size-4 shrink-0 opacity-50" />
    <Command.Input
      ref={ref}
      className={twMerge(
        'flex w-full rounded-md border-none bg-transparent px-0 py-3 text-sm ring-0 outline-hidden placeholder:text-neutral-500 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  </div>
));
Search.displayName = 'Combobox.Search';

const Content = forwardRef<
  ElementRef<typeof PopoverContent>,
  ComponentProps<typeof PopoverContent> & {
    placeholder?: string;
    showSearch?: boolean;
    emptyLabel?: string;
  }
>(
  (
    { className, children, emptyLabel, placeholder, showSearch, ...props },
    ref,
  ) => (
    <PopoverContent
      ref={ref}
      asChild
      className={twMerge(
        'group relative z-50 mt-0.5 max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) max-w-(--radix-dropdown-menu-content-available-width) min-w-32 origin-(--radix-dropdown-menu-content-transform-origin) overflow-auto rounded-md border bg-white shadow-md outline-none',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      )}
      {...props}
    >
      {/*
       * Setting to a default value of "__empty__" to ensure the Command component
       * doesn't attempt to auto-scroll to the first item. Which causes issues when the
       * app is embedded in an iframe and not scrollable.
       * Tried setting the defaultValue to the actual value, but it causes the same issue.
       *
       * related github issues:
       * - https://github.com/pacocoursey/cmdk/issues/317
       * - https://github.com/pacocoursey/cmdk/issues/171
       */}
      <Command defaultValue="__empty__" label={placeholder}>
        {showSearch && <Search placeholder={placeholder} />}
        <Command.List className="max-h-[300px] overflow-x-hidden overflow-y-auto">
          <Command.Empty className="py-4 text-center text-sm text-pretty">
            {emptyLabel}
          </Command.Empty>
          <Command.Group className="p-1">{children}</Command.Group>
        </Command.List>
      </Command>
    </PopoverContent>
  ),
);
Content.displayName = 'Combobox.Content';

const Item = forwardRef<
  ElementRef<typeof Command.Item>,
  ComponentProps<typeof Command.Item>
>(({ className, children, ...props }, ref) => (
  <Command.Item
    ref={ref}
    className={twMerge(
      'group flex w-full cursor-default items-center justify-between gap-2 rounded-xs px-2 py-1.5 text-sm outline-hidden select-none',
      'data-[disabled="true"]:pointer-events-none data-[disabled="true"]:opacity-50 data-[selected="true"]:bg-gray-100',
      className,
    )}
    {...props}
  >
    {children}
    <Icon
      icon="Check"
      className="size-4 shrink-0 group-data-[checked=false]:invisible group-data-[checked=true]:visible"
    />
  </Command.Item>
));
Item.displayName = 'Combobox.Item';

/**
 * The `value` of each option is expected to be unique, as it is also used as the `key`.
 * The `label` can be any `ReactNode` to represent the option in the UI; however, it's suggested to keep it mostly prose.
 */
type ComboboxOption<T extends string = string> = {
  value: T;
  keywords?: string[];
  label: NonNullable<ReactNode>;
};

type ValidationConstraint = Extract<keyof ValidityState, 'valueMissing'>;
type ComboboxProps<T extends string> = {
  /**
   * The unique identifier for the combobox trigger.
   * If not provided, a unique ID will be generated.
   */
  id?: string;
  /**
   * The name of the combobox, used for form submission.
   * This is required for the combobox to be included in form data.
   */
  name: string;
  /**
   * The label for the combobox trigger.
   */
  label: string;
  /**
   * The options to display in the combobox.
   */
  options: ComboboxOption<T>[];
  /*
   * The current value of the combobox.
   * If not provided, the `defaultValue` will be used.
   */
  value?: T;
  /**
   * The default value of the combobox.
   */
  defaultValue?: T;
  /**
   * Callback function that is called when the value changes.
   */
  onChange?: NoInfer<(value?: T) => void>;
  /**
   * The `autocomplete` attribute for the combobox input.
   * This is used to control browser autofill behavior.
   * It can be set to `off`, `on`, or any other valid value.
   * Note: Setting this to `off` may not work in all browsers due to browser
   * autofill heuristics.
   */
  autoComplete?: AutoFill;
  /**
   * An optional description for the combobox.
   * This will be displayed below the combobox trigger.
   * It can be used to provide additional context or instructions.
   * If not provided, no description will be displayed.
   * If you want to display an error message, use the `error` prop instead.
   */
  description?: string;
  /**
   * An object containing validation messages for the combobox.
   * The keys should match the `ValidityState` properties, such as `valueMissing`.
   * If a validation message is not provided, a default message will be used.
   * This is used to provide custom validation messages for the combobox.
   */
  validationMessages?: Partial<Record<ValidationConstraint, string>>;
  /**
   * The label to display when no options match the search query.
   * This is only used when `showSearch` is true and no options are found.
   * If not provided, a default message will be used.
   */
  emptyLabel?: string;
  /**
   * Whether to show the search input in the combobox.
   * If true, a search input will be displayed at the top of the options list.
   * This is useful when there are many options to choose from.
   * Defaults to true if there are more than 10 options.
   */
  showSearch?: boolean;
  /**
   * The placeholder text for the search input.
   * This is only used when `showSearch` is true.
   * If not provided, a default placeholder will be used.
   */
  placeholder?: string;
  /**
   * Whether the combobox is required.
   * If true, the combobox will be marked as required and will show validation errors
   * if no value is selected.
   */
  required?: boolean;
  /**
   * The alignment of the combobox content.
   * This is passed to the Radix Popover component.
   * It can be set to 'start', 'center', or 'end'.
   * Defaults to 'start'.
   */
  align?: ComponentProps<typeof PopoverContent>['align'];
  /**
   * An optional className to apply to the combobox root element.
   * This can be used to apply custom styles or layout.
   */
  className?: string;
  /**
   * An optional error message to display below the combobox.
   * If provided, this will override the description and display as an error.
   * This is useful for displaying validation errors or other messages.
   */
  error?: string;

  /**
   * Whether the combobox is disabled.
   * If true, the combobox will be disabled and not interactive.
   * This is useful for making the combobox read-only or when it should not be editable.
   */
  disabled?: boolean;
};

const findOption =
  <T extends string>(options: ComboboxOption<T>[]) =>
  (value?: string) =>
    options.find(
      (o) =>
        // check value
        o.value === value ||
        // check if the value matches the label
        (typeof o.label === 'string' &&
          o.label.toLowerCase() === value?.toLowerCase()),
      // todo check if label is a jsx element and compare the text content
    ) ?? null;

export default function Combobox<T extends string>(
  props: ComboboxProps<T>,
): JSX.Element {
  const {
    name,
    label,
    options,
    onChange,
    required = false,
    emptyLabel = 'Nothing found.',
    placeholder = 'Search or select an option',
    showSearch = options.length > 10,
    description,
    validationMessages,
    align,
    className,
    autoComplete,
    disabled = false,
  } = props;

  const id = useUniqueId(props?.id);
  const descriptionId = useUniqueId();
  const errorId = useUniqueId();
  const getOption = findOption(options);

  const [state, setState] = useState({
    selectedOption: getOption(props?.value ?? props?.defaultValue),
    showContent: false,
    error: props?.error ?? null,
  });
  const fieldRef = useRef<HTMLInputElement>(null);

  // Event Handlers
  /**
   * This function is called when the popover content is opened or closed
   * It updates the state to show or hide the content
   */
  const handleOpenChange = (open: boolean) => {
    setState((prev) => ({
      ...prev,
      showContent: open,
    }));
  };

  /**
   * This function is called when an item is selected from the combobox.
   * It updates the selected option in the state, clears any error messages, closes the popover,
   * and calls the `onChange` callback with the selected value.
   * If the fieldRef is set, it also updates the custom validity state and auto-fill data attribute.
   */
  const handleItemSelect = (value: string) => {
    const option = getOption(value);

    setState((prev) => ({
      ...prev,
      selectedOption: option,
      error: null,
      showContent: false,
    }));

    if (fieldRef.current) {
      // see the comment in handleOnChange about the data attribute
      fieldRef.current.dataset.autofill = 'false';
      fieldRef.current.setCustomValidity('');
    }

    onChange?.(option?.value);
  };

  /**
   * This function is called when the input is invalid.
   * It sets a custom validity message based on the validationMessages prop or a default message.
   * It also updates the state to show the error message.
   */
  const handleOnInvalid = (event: FormEvent<HTMLInputElement>) => {
    const message =
      validationMessages?.valueMissing ??
      'Please select an option from the list.';

    event.currentTarget.setCustomValidity(message);

    setState((prev) => ({
      ...prev,
      error: message,
    }));
  };

  /**
   * This function is called when the input value changes. This is to handle the browser autofill behavior.
   * When the input value is updated by state change, it will not trigger the onChange event.
   *
   * Setting a data attribute on the input to indicate whether the value was auto-filled is needed to
   * internally manage whether the input should be shown or not to provide the auto-fill styling.
   * The `:autofill` pseudo-class doesn't not get cleared when the value is set programmatically
   * and if an `input` or `change` event is triggered.
   * It seem like this pseudo-class is only removed when the user interacts with the input field,
   * so we need to manage it manually.
   *
   * Roughly based on [Base UI Select](https://github.com/mui/base-ui/blob/a2e10fa110733fad39d2003a7b9ba18421bd9317/packages/react/src/select/root/SelectRoot.tsx#L101)
   */
  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Workaround for https://github.com/facebook/react/issues/9023
    if (event.nativeEvent.defaultPrevented) {
      return;
    }

    const { target } = event;
    const autofillValue = target.value;

    // checking if the clear event and the input not auto-filled to preserve any user selection
    if (target.dataset.autofill === 'false' && autofillValue === '') {
      target.value = state.selectedOption?.value ?? '';
      return;
    }

    // Find the exact option to ensure it exists for the value coming from autofill will be null if auto-fill clear event i.e. empty string
    const exactOption = getOption(autofillValue);
    // update the input value with exact option value or leave it as is
    target.value = exactOption?.value ?? autofillValue;
    // set the auto-fill data attribute to true if autofill value is not empty or false if the value is cleared
    target.dataset.autofill = autofillValue ? 'true' : 'false';
    // set the custom validity if the auto fill value is not in the options, clear validity if the auto-fill value is empty (ie clear event)
    // ! IMPORTANT: this needs to be set if the auto-filled value is not in the options to prevent the form from being submitted with an invalid value
    target.setCustomValidity(
      exactOption || autofillValue === '' ? '' : 'Invalid auto fill value',
    );

    const selectedOption =
      autofillValue ?
        (exactOption ??
        // create an option to display value so people can see what was auto-filled
        ({
          value: 'INVALID_AUTOFILL_VALUE' as T,
          label: autofillValue,
        } satisfies ComboboxOption<T>))
      : null;

    setState((prev) => ({
      ...prev,
      selectedOption,
      error: null,
    }));

    // Notify the parent component of the change with only the exact option value and not an invalid autofill value
    onChange?.(exactOption?.value);
  };

  return (
    <Field.Root className={className}>
      <Popover open={state.showContent} onOpenChange={handleOpenChange}>
        <div className="relative">
          {/**
           * This input is used to connect into the validation API and autofill
           * It is can not be aria-hidden to allow the browser to autofill it
           * It is also providing the auto-fill styling behind the trigger
           */}
          <input
            tabIndex={-1}
            className={twMerge(
              // the 7px border-radius is to match the trigger inner border radius
              'absolute inset-0 rounded-[7px] border-none text-transparent outline-0 data-[autofill=false]:sr-only',
              // This is a workaround to override the autofill style (https://noahliebman.net/2024/08/beating-important-user-agent-styles/)
              'autofill:transition-[color] autofill:duration-[calc(infinity*1s)] autofill:ease-[step-end]',
            )}
            required={required}
            name={name}
            autoComplete={autoComplete}
            value={state.selectedOption?.value ?? ''}
            ref={fieldRef}
            onChange={handleOnChange}
            onInvalid={handleOnInvalid}
          />
          <Trigger
            disabled={disabled}
            id={id}
            aria-describedby={`${errorId} ${descriptionId}`}
            data-valid={!state.error}
            data-empty={!state.selectedOption}
            label={label}
          >
            {state.selectedOption?.label}
          </Trigger>
        </div>

        <Content
          align={align}
          placeholder={placeholder}
          emptyLabel={emptyLabel}
          showSearch={showSearch}
        >
          {options.map((option) => (
            <Item
              key={option.value}
              value={option.value}
              keywords={
                option.keywords ??
                (typeof option.label === 'string' ? [option.label] : undefined)
              }
              onSelect={handleItemSelect}
              data-checked={state.selectedOption?.value === option.value}
            >
              {option.label}
            </Item>
          ))}
        </Content>
      </Popover>

      {state.error && (
        <Field.ErrorText id={errorId}>{state.error}</Field.ErrorText>
      )}
      {description && !state.error && (
        <Field.HelpText id={descriptionId}>{description}</Field.HelpText>
      )}
    </Field.Root>
  );
}
